import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useAppSelector, selectIsAuthenticated, selectPermissions, selectUser } from '@/app/store';
import { toPermissionSet, can, canAny } from './permissions';

/**
 * Route guards (SRS §7.2, FR-35).
 *
 * These are a USABILITY layer, not a security boundary — they stop a user
 * navigating to a page whose data they'd be denied anyway. The API enforces the
 * real check on every request; a user who edits their local state gains nothing
 * but an empty page full of 403s.
 */

/** Blocks unauthenticated access, remembering where the user was headed. */
export function RequireAuth() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    // `state.from` lets the login page return the user to their target.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

/** Redirects an already-signed-in user away from login/signup. */
export function RequireGuest() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  return isAuthenticated ? <Navigate to="/" replace /> : <Outlet />;
}

/**
 * Gate for the cross-tenant super-admin console. A usability layer only — the
 * API re-checks the flag on every platform route (requirePlatformAdmin), so a
 * user who forces their way here just gets 403s.
 */
export function RequirePlatformAdmin() {
  const user = useAppSelector(selectUser);
  return user?.isPlatformAdmin ? <Outlet /> : <Navigate to="/" replace />;
}

/** True for a login that only holds the external client/vendor portal role(s). */
function isPortalOnly(roles: string[] | undefined) {
  return !!roles?.length && roles.every((r) => r === 'client' || r === 'vendor');
}

/**
 * Gate for the external Client/Vendor portal (`/portal/*`) — only a
 * client/vendor-role login may enter; a staff login is bounced to `/`.
 */
export function RequirePortalRole() {
  const user = useAppSelector(selectUser);
  return isPortalOnly(user?.roles) ? <Outlet /> : <Navigate to="/" replace />;
}

/**
 * Gate for the internal AppShell — a portal-only login is bounced to
 * `/portal` instead (they hold none of the internal resource permissions
 * anyway, so this just avoids a wall of 403s in the wrong chrome).
 */
export function RequireStaffRole() {
  const user = useAppSelector(selectUser);
  return isPortalOnly(user?.roles) ? <Navigate to="/portal" replace /> : <Outlet />;
}

interface RequirePermissionProps {
  /** Satisfied when ANY of these is held (e.g. read or manage). */
  anyOf: string[];
  children?: ReactNode;
  /** Rendered instead of redirecting when the check fails. */
  fallback?: ReactNode;
}

/** Route-level permission gate. */
export function RequirePermission({ anyOf, children, fallback }: RequirePermissionProps) {
  const permissions = useAppSelector(selectPermissions);
  const allowed = canAny(toPermissionSet(permissions), anyOf);

  // Forward the outlet context. React Router's <Outlet> ALWAYS provides its
  // `context` prop — so a bare <Outlet /> here would publish `undefined` and
  // clobber the value an ancestor layout passed down, silently breaking any
  // child that reads useOutletContext(). Null when there's no ancestor.
  const context = useOutletContext();

  if (!allowed) {
    return fallback !== undefined ? <>{fallback}</> : <Navigate to="/403" replace />;
  }
  return <>{children ?? <Outlet context={context} />}</>;
}

interface CanProps {
  /** A single "resource:action" requirement. */
  perform: string;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Conditionally render a control the user is allowed to use.
 * Prefer this over scattering `useAuth().can()` through JSX.
 */
export function Can({ perform, children, fallback = null }: CanProps) {
  const permissions = useAppSelector(selectPermissions);
  return can(toPermissionSet(permissions), perform) ? <>{children}</> : <>{fallback}</>;
}
